import React, { useEffect, useState } from 'react';
import DropdownBase, { DropdownProps } from 'react-overlays/Dropdown';
import { DropdownContext, IDropdownBaseProps } from './DropdownContext';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { getHexOpacity, resolveProps } from '../../common/utils';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { BaseDiv } from '../../common/BaseElements';

export const dropdownColours = ({ base, palette, primary }: IColoursCollection) => ({
  dropdownColours: {
    suffix: {
      text: palette.neutral600
    },
    disabled: {
      opacity: 0.5
    },
    item: {
      hover: {
        background: `${base.black.colour}${getHexOpacity(5)}`
      },
      active: {
        background: `${base.black.colour}${getHexOpacity(10)}`,
        border: `${PX_TO_REM[4]} solid ${primary.colour}`
      }
    }
  }
});

const DropdownContainer = styled(BaseDiv)``;

export interface IDropdownProps extends Omit<DropdownProps, 'onToggle'>, IDropdownBaseProps {
  onToggle?: DropdownProps['onToggle'];
  onShowChg?: (show: boolean) => any;
  hoverable?: boolean;
}

export const Dropdown: React.FC<IDropdownProps> = ({ children, onToggle, onShowChg, debounceInMs = 0, hoverable, defaultShow, show, ...props }) => {
  const defaultProps = useWindowDimensions();
  // Allows us to override windowsDimension
  const { isTablet, isMobile, asMenu } = resolveProps({ ...defaultProps }, { ...props });
  const { generateTestId } = useTestId(props, 'Dropdown');
  const userControlled = show !== undefined;

  const [hovered, setHovered] = useState<boolean | undefined>(false);
  const [pinned, setPinned] = useState(defaultShow);
  const [showFlag, setShowFlag] = useState(defaultShow);

  useEffect(() => {
    if (showFlag !== undefined) onShowChg?.(showFlag);
  }, [showFlag]);

  const handleOnClick = () => {
    if (pinned) {
      setPinned(false);
    }
  };

  const handleOnToggle: DropdownProps['onToggle'] = (nextShow, event) => {
    if (hovered) {
      setPinned(true);
      // eslint-disable-next-line unicorn/no-useless-undefined
      setHovered(undefined);
      setShowFlag(true);
    } else {
      setPinned(nextShow);
      setShowFlag(nextShow);
    }
    onToggle?.(nextShow, event);
  };

  const debouncedHandleMouseOut = debounce(() => {
    if (pinned !== true) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      setPinned(undefined);
      setHovered(false);
      setShowFlag(false);
    }
  }, debounceInMs);

  const handleMouseOver = () => {
    if (pinned !== true) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      setPinned(undefined);
      setHovered(true);
      setShowFlag(true);
    }
    debouncedHandleMouseOut.cancel();
  };

  return (
    <DropdownContext.Provider value={{ isMobile, isTablet, debounceInMs, asMenu, hovered, setHovered, setPinned }}>
      <DropdownContainer
        onClick={userControlled ? undefined : handleOnClick}
        onMouseEnter={hoverable && !isMobile && !userControlled ? handleMouseOver : undefined}
        onMouseLeave={hoverable && !isMobile && !userControlled ? debouncedHandleMouseOut : undefined}
        {...generateTestId()}
      >
        <DropdownBase onToggle={handleOnToggle} show={show ?? pinned ?? hovered} defaultShow={defaultShow} {...props}>
          {children}
        </DropdownBase>
      </DropdownContainer>
    </DropdownContext.Provider>
  );
};

export { useDropdownMenu } from 'react-overlays/DropdownMenu';
export { useDropdownToggle } from 'react-overlays/DropdownToggle';
export { default as ReactDropdown } from 'react-overlays/Dropdown';
